import React, { useEffect } from 'react';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Button,
  Box,
  Typography,
  Grid,
  TextField,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ViewTimelineIcon from '@mui/icons-material/ViewTimeline';

import { Formik, Form, useFormikContext } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import { useResponseHandler } from '../../../../hooks/useResponseHandler';
import { storeUpdateShiftTemplate } from '../../../../store/shift_templates';
import { validationSchema } from '../formik/plantilla_turnos/validationSchema';
import { PlantillaTurnoFormModel } from '../formik/plantilla_turnos/PlantillaTurnoFormModel';

import InputField from '../../../../components/formFields/InputField';
import { mapFormToShiftTemplate, mapShiftTemplateToForm } from '../mappers/plantillaTurnos.mapper';
import AutocompleteField from 'components/formFields/autocompleteField';

const DurationCalculator = () => {
  const { values, setFieldValue } = useFormikContext<any>();

  React.useEffect(() => {
    if (values.startTime && values.endTime) {
      const [startH, startM] = values.startTime.split(':').map(Number);
      const [endH, endM] = values.endTime.split(':').map(Number);

      let diffMinutes = endH * 60 + endM - (startH * 60 + startM);
      if (diffMinutes < 0) {
        diffMinutes += 24 * 60; // Overnight shift
      }

      const hours = Number((diffMinutes / 60).toFixed(2));

      if (values.durationHours !== hours) {
        setFieldValue('durationHours', hours);
      }
    }
  }, [values.startTime, values.endTime, setFieldValue, values.durationHours]);

  return null;
};

export const EditPlantillaTurnoModal = ({
  open,
  setOpen,
  reloadTable,
  item,
  isViewMode = false,
}: any) => {
  const dispatch: any = useDispatch();
  const { handleResponse, ResponseComponents } = useResponseHandler();

  const formikProps: any = useFormikContext();

  const { cargos_select } = useSelector((state: any) => state.cargos);

  const { formField } = PlantillaTurnoFormModel;

  const initialValues = mapShiftTemplateToForm(item);

  const handleSubmit = async (values: any, actions: any) => {
    try {
      const dataToSave = mapFormToShiftTemplate(values);
      const response = await dispatch(storeUpdateShiftTemplate(item.id, dataToSave));
      await handleResponse(response, 'Editar Plantilla Turno', true);
      if (response?.ok) {
        setTimeout(() => {
          setOpen(false);
        }, 1500);
      }
    } catch (error) {
      console.error(error);
    } finally {
      actions.setSubmitting(false);
    }
  };

  return (
    <>
      {ResponseComponents}
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow:
              '0px 12px 24px -4px rgba(145, 158, 171, 0.12), 0px 0px 2px 0px rgba(145, 158, 171, 0.2)',
          },
        }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ p: 1, borderRadius: 2, display: 'flex' }}>
            <ViewTimelineIcon color="primary" />
          </Box>
          <Box>
            <Typography variant="h6">
              {isViewMode ? 'Detalle Plantilla de Turnos' : 'Editar Plantilla de Turnos'}
            </Typography>
            <Typography variant="body2" color="#32475C99">
              {isViewMode
                ? 'Información del bloque de horario.'
                : 'Modifica la información del bloque de horario.'}
            </Typography>
          </Box>
          <IconButton
            aria-label="close"
            onClick={() => setOpen(false)}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {({ isSubmitting, isValid, dirty, values }) => (
            <Form>
              <DurationCalculator />
              <DialogContent
                dividers
                sx={{ borderTop: '1px solid #32475C1F', borderBottom: '1px solid #32475C1F' }}
              >
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12 }}>
                    <InputField
                      fullWidth
                      name={formField.name.name}
                      label={formField.name.label}
                      InputProps={{ readOnly: isViewMode }}
                    />
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    {isViewMode ? (
                      <TextField
                        fullWidth
                        label={formField.position.label}
                        value={values.position?.label || ''}
                        InputProps={{ readOnly: true }}
                      />
                    ) : (
                      <AutocompleteField
                        name={formField.position.name}
                        label={formField.position.label}
                        options={cargos_select}
                        fullWidth
                        placeholder="Selecciona un cargo"
                      />
                    )}
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    <InputField
                      fullWidth
                      type="time"
                      name={formField.startTime.name}
                      label={formField.startTime.label}
                      InputLabelProps={{ shrink: true }}
                      InputProps={{ readOnly: isViewMode }}
                    />
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    <InputField
                      fullWidth
                      type="time"
                      name={formField.endTime.name}
                      label={formField.endTime.label}
                      InputLabelProps={{ shrink: true }}
                      InputProps={{ readOnly: isViewMode }}
                    />
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <InputField
                      readOnly
                      fullWidth
                      type="number"
                      name={formField.durationHours.name}
                      label={formField.durationHours.label}
                    />
                  </Grid>
                </Grid>

                {!isViewMode && (
                  <Box
                    sx={{
                      mt: 3,
                      p: 2,
                      borderRadius: 2,
                      bgcolor: 'rgba(105, 108, 255, 0.08)',
                      display: 'flex',
                      gap: 1,
                    }}
                  >
                    <InfoOutlinedIcon color="primary" />
                    <Typography variant="body2" color="primary">
                      Asegúrate de que la duración en horas corresponda al intervalo de tiempo
                      ingresado.
                    </Typography>
                  </Box>
                )}
              </DialogContent>
              <DialogActions sx={{ p: 3 }}>
                <Button
                  onClick={() => setOpen(false)}
                  color="inherit"
                  sx={{ borderRadius: 2, textTransform: 'none' }}
                >
                  {isViewMode ? 'Cerrar' : 'Cancelar'}
                </Button>
                {!isViewMode && (
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={isSubmitting || !isValid || !dirty}
                    sx={{ borderRadius: 2, textTransform: 'none' }}
                  >
                    Guardar Cambios
                  </Button>
                )}
              </DialogActions>
            </Form>
          )}
        </Formik>
      </Dialog>
    </>
  );
};
